/**
 * Organizational Chart — pure helpers shared by the staff view and the HR editor.
 * Items come from the API as a flat list: { id, parent_id, kind, user_id, name, title, details,
 * headcount, photo_url, position, level?, left? }. parent_id null = top level.
 */

/**
 * EVOX levels that count as HR (same list as the Sidebar's HR menu, minus Admin). The server gate
 * (User::isLevel('HR')) matches all of them, so the page, the menu link and the Manage charts
 * button must too.
 */
export const HR_LEVELS = ["HR", "PH_HR", "IND_HR", "MAR_HR"];

/** Children of `parentId` (null = top level), in display order. */
export const childrenOf = (items, parentId) =>
  items
    .filter((i) => (i.parent_id === undefined ? null : i.parent_id) === parentId)
    .sort((a, b) => (a.position - b.position) || (a.id - b.id));

/** { itemId: level } with level 1 at the top. */
export const levelsOf = (items) => {
  const byId = {};
  items.forEach((i) => { byId[i.id] = i; });
  const out = {};
  items.forEach((i) => {
    let level = 1;
    let cur = i.parent_id;
    let guard = 0;
    while (cur !== null && cur !== undefined && byId[cur] && guard++ < 1000) {
      level++;
      cur = byId[cur].parent_id;
    }
    out[i.id] = level;
  });
  return out;
};

/** Item ids from the item's manager up to the top. */
export const chainOf = (items, id) => {
  const byId = {};
  items.forEach((i) => { byId[i.id] = i; });
  const out = [];
  let cur = byId[id] ? byId[id].parent_id : null;
  let guard = 0;
  while (cur !== null && cur !== undefined && byId[cur] && guard++ < 1000) {
    out.push(cur);
    cur = byId[cur].parent_id;
  }
  return out;
};

/** Other items on the same level under the same manager; top-level items are peers of each other. */
export const peersOf = (items, id) => {
  const me = items.find((i) => i.id === id);
  if (!me) return [];
  const parent = me.parent_id === undefined ? null : me.parent_id;
  return items.filter((i) => i.id !== id && (i.parent_id === undefined ? null : i.parent_id) === parent);
};

/** [{label, value}] for the chart's detail rows that have a value for this item, in row order. */
export const detailLines = (item, rows) =>
  (rows || [])
    .map((r) => ({ id: r.id, label: r.label, value: item && item.details ? item.details[r.id] : null }))
    .filter((l) => l.value);

/** Every item below `id`. */
export const descendantsOf = (items, id) => {
  const out = [];
  const queue = [id];
  while (queue.length) {
    const cur = queue.shift();
    items.forEach((i) => {
      if (i.parent_id === cur) { out.push(i.id); queue.push(i.id); }
    });
  }
  return out;
};

/** True when nobody in the tree has a manager (e.g. Executive Committee). */
export const isFlat = (items) => items.length > 0 && items.every((i) => i.parent_id === null || i.parent_id === undefined);

/** "Mae Torres" -> "MT"; team / open boxes get no initials. */
export const initials = (name) =>
  String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .join("")
    .slice(0, 2);

/**
 * { user_id: [chart names] } listing, for every person, the OTHER published charts they are in
 * (tree or side group). Used for the "Also in" links.
 */
export const alsoInMap = (charts) => {
  const where = {};
  charts.forEach((c) => {
    const ids = new Set();
    (c.items || []).forEach((i) => { if (i.user_id) ids.add(i.user_id); });
    (c.groups || []).forEach((g) => (g.items || []).forEach((i) => { if (i.user_id) ids.add(i.user_id); }));
    ids.forEach((uid) => { (where[uid] = where[uid] || []).push({ id: c.id, name: c.name }); });
  });
  return where;
};

/** Items whose name or title contains the text (case-insensitive). */
export const searchItems = (items, text) => {
  const q = String(text || "").trim().toLowerCase();
  if (q.length < 2) return [];
  return items.filter((i) => String(i.name || "").toLowerCase().indexOf(q) !== -1 || String(i.title || "").toLowerCase().indexOf(q) !== -1);
};

/** Columns for a flat chart: up to 6 in a row, larger sets split evenly. */
export const flatColumns = (count) => (count <= 6 ? count : Math.ceil(count / Math.ceil(count / 6)));

/** "2026-10-15T03:30:00+00:00" -> "15 October 2026"; null for bad input. */
export const formatDate = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? null : d.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
};

/** People count shown under a chart's name (people + team headcounts, no open positions). */
export const peopleCount = (chart) => {
  const count = (list) => list.reduce((n, i) => n + (i.kind === "person" ? 1 : i.kind === "team" ? (i.headcount || 0) : 0), 0);
  return count(chart.items || []) + (chart.groups || []).reduce((n, g) => n + count(g.items || []), 0);
};

/** 422 body -> one readable line (error.message may be an array, an object or a string). */
export const errorText = (e) => {
  const m = e && e.data && e.data.error && e.data.error.message;
  if (Array.isArray(m)) return m.join(" ");
  if (m && typeof m === "object") return [].concat(...Object.values(m)).join(" ");
  if (typeof m === "string" && m) return m;
  return "Something went wrong. Please try again.";
};
